import React, { useState } from 'react';
import { CoachClientList } from '../dashboard/CoachClientList';
import { DashboardPage } from '../../pages/DashboardPage';
import { NutritionOverviewPage } from '../../pages/NutritionOverviewPage';
import { GoalsProgressPage } from '../../pages/GoalsProgressPage';
import { ReportsPage } from '../../pages/ReportsPage';
import { ClientProfilePage } from '../../pages/ClientProfilePage';
import { TherapeuticTargetsPage } from '../../pages/TherapeuticTargetsPage';
import { ClientActivityPage } from '../../pages/ClientActivityPage';
import { DailyFoodEntriesPage } from '../../pages/DailyFoodEntriesPage';
import { MessagingPage } from '../../pages/MessagingPage';
import { MealPlanningPage } from '../../pages/MealPlanningPage';
import { MovementPage } from '../../pages/MovementPage';
import { AgentInsightsPage } from '../../pages/AgentInsightsPage';
import { EmailTemplatesPage } from '../../pages/EmailTemplatesPage';

interface CoachInterfaceProps {
  user: any;
  onLogout: () => void;
}

export const CoachInterface: React.FC<CoachInterfaceProps> = ({ user, onLogout }) => {
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [isAdminMode, setIsAdminMode] = useState<boolean>(false);
  const [selectedView, setSelectedView] = useState<string>('dashboard');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);

  const handleClientSelect = (clientId: number, clientName: string) => {
    setSelectedClient({ id: clientId, full_name: clientName });
    setIsAdminMode(false);
    setSelectedView('dashboard');
  };

  const handleAdminSelect = () => {
    setSelectedClient(null);
    setIsAdminMode(true);
    setSelectedView('insights');
  };

  // Client-specific views — shown when a client is selected
  const clientViewGroups = [
    { label: 'Overview', views: [
      { id: 'dashboard', label: 'Dashboard', icon: '📊', component: DashboardPage },
    ]},
    { label: 'Nutrition', views: [
      { id: 'nutrition', label: 'Nutrition', icon: '🥗', component: NutritionOverviewPage },
      { id: 'daily-foods', label: 'Foods', icon: '🍽️', component: DailyFoodEntriesPage },
      { id: 'meal-planning', label: 'Meal Plan', icon: '📋', component: MealPlanningPage },
      { id: 'targets', label: 'Targets', icon: '⚡', component: TherapeuticTargetsPage },
    ]},
    { label: 'Wellness', views: [
      { id: 'goals', label: 'Goals', icon: '🎯', component: GoalsProgressPage },
      { id: 'movement', label: 'Movement', icon: '🏃', component: MovementPage },
    ]},
    { label: 'Client', views: [
      { id: 'profile', label: 'Profile', icon: '👤', component: ClientProfilePage },
    ]},
  ];

  // Admin views — shown when Admin is selected
  const adminViewGroups = [
    { label: 'Overview', views: [
      { id: 'insights', label: 'Insights', icon: '🧠', component: AgentInsightsPage },
      { id: 'client-activity', label: 'Activity', icon: '📅', component: ClientActivityPage },
    ]},
    { label: 'Communication', views: [
      { id: 'messages', label: 'Messages', icon: '💬', component: MessagingPage },
      { id: 'reports', label: 'Reports', icon: '📈', component: ReportsPage },
    ]},
    { label: 'Settings', views: [
      { id: 'email-templates', label: 'Email Templates', icon: '✉️', component: EmailTemplatesPage },
    ]},
  ];

  const activeViewGroups = isAdminMode ? adminViewGroups : clientViewGroups;

  // Flat list for component lookup (all views)
  const allViews = [...clientViewGroups, ...adminViewGroups].flatMap(g => g.views);

  const CurrentViewComponent = allViews.find(v => v.id === selectedView)?.component || DashboardPage;

  return (
    <div className="h-screen flex bg-gray-100">
      {/* Client Sidebar */}
      <div className={`${isSidebarCollapsed ? 'w-0' : 'w-80'} bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out overflow-hidden`}>
        {/* Coach Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
          <h1 className="text-xl font-bold text-white">Coach Dashboard</h1>
          <p className="text-blue-100 text-sm mt-1">
            Welcome, {user?.full_name || user?.email || 'Coach'}
          </p>
          <button
            onClick={onLogout}
            className="mt-3 text-xs text-blue-200 hover:text-white transition-colors"
          >
            Sign Out
          </button>
        </div>

        {/* Client List */}
        <div className="flex-1 overflow-y-auto">
          <CoachClientList
            onClientSelect={handleClientSelect}
            onAdminSelect={handleAdminSelect}
            selectedClientId={selectedClient?.id}
            isAdminMode={isAdminMode}
          />
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex relative">
        <div className="flex-1 flex flex-col">
        {/* Sidebar Toggle Button */}
        <button
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className="absolute left-4 top-4 z-10 bg-white border border-gray-300 rounded-md p-2 shadow-sm hover:bg-gray-50 transition-colors"
          title={isSidebarCollapsed ? "Show client list" : "Hide client list"}
        >
          <svg
            className={`w-5 h-5 text-gray-600 transition-transform duration-300 ${isSidebarCollapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>

        {selectedClient || isAdminMode ? (
          <>
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 pl-16">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {isAdminMode ? 'Admin' : selectedClient?.full_name}
                  </h2>
                  <span className="text-xs text-gray-400">
                    {allViews.find(v => v.id === selectedView)?.icon}{' '}
                    {allViews.find(v => v.id === selectedView)?.label}
                  </span>
                </div>
              </div>

              {/* Grouped View Navigation */}
              <div className="flex items-center gap-0.5 flex-wrap">
                {activeViewGroups.map((group, gi) => (
                  <React.Fragment key={group.label}>
                    {gi > 0 && (
                      <div className="w-px h-5 bg-gray-200 mx-1.5 shrink-0" />
                    )}
                    {group.views.map((view) => (
                      <button
                        key={view.id}
                        onClick={() => setSelectedView(view.id)}
                        className={`px-2 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                          selectedView === view.id
                            ? 'bg-blue-100 text-blue-700'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="mr-0.5">{view.icon}</span>
                        {view.label}
                      </button>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <CurrentViewComponent
                user={isAdminMode || selectedView === 'messages' ? user : (selectedClient || user)}
                coachMode={true}
                clientId={selectedClient?.id}
                clientName={selectedClient?.full_name}
                onNavigate={setSelectedView}
              />
            </div>
          </>
        ) : (
          /* No Client Selected, No Admin Mode */
          <div className="flex-1 flex items-center justify-center pl-16">
            <div className="text-center">
              <div className="text-6xl mb-6">👥</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Select a Client
              </h2>
              <p className="text-gray-600 max-w-md">
                {isSidebarCollapsed ? (
                  <>Click the <span className="font-semibold">{'<<'}</span> button to show the client list</>
                ) : (
                  <>Choose a client from the sidebar, or click <strong>Admin</strong> for general views.</>
                )}
              </p>
            </div>
          </div>
        )}
        </div>

      </div>
    </div>
  );
};